import { addDays, differenceInMinutes, startOfDay } from "date-fns";
import { CalendarClock, ChevronRight } from "lucide-react";
import { useTranslate } from "ra-core";
import { useMemo } from "react";
import { Link } from "react-router";

import { MOBILE_CALENDAR_PATH } from "../../layout/mobileModules";
import { dayKey } from "../../mobile/mobileModel";
import { useAgendaEntries } from "../../mobile/useAgendaEntries";
import { AttentionGroups, AttentionLoading } from "./AttentionInbox";
import { CollectCard } from "./CollectCard";
import { nextUp } from "./homeModel";
import { TodayCounters } from "./TodayCounters";
import { useHomeToday } from "./useHomeData";
import { useHomeFormat } from "./useHomeFormat";

const SectionHead = ({
  id,
  title,
  to,
  link,
}: {
  id: string;
  title: string;
  to: string;
  link: string;
}) => (
  <div className="home-m__head">
    <h2 id={id}>{title}</h2>
    <Link to={to}>{link}</Link>
  </div>
);

/** «Siguiente»: the event or timed item in progress or next to start. */
const NextCard = () => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const now = useMemo(() => new Date(), []);
  const today = useMemo(() => startOfDay(now), [now]);
  const { entries, pending } = useAgendaEntries(today, addDays(today, 7));
  const next = nextUp(entries, now);
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.next.${key}`, options);

  if (pending)
    return <div className="home-next home-next--empty" aria-busy="true" />;
  if (!next) {
    return (
      <div className="home-next home-next--empty">
        <CalendarClock aria-hidden className="size-5" strokeWidth={1.75} />
        <span>{t("empty")}</span>
      </div>
    );
  }
  const minutes = differenceInMinutes(next.start, now);
  const when =
    minutes <= 0
      ? t("now")
      : minutes < 60
        ? t("in_minutes", { smart_count: minutes })
        : null;
  const day =
    dayKey(next.start) === dayKey(now)
      ? t("today")
      : dayKey(next.start) === dayKey(addDays(now, 1))
        ? t("tomorrow")
        : format.shortDate(next.start);
  const range = next.end
    ? `${format.time(next.start)} – ${format.time(next.end)}`
    : format.time(next.start);
  const body = (
    <>
      <span className="home-next__meta">
        <CalendarClock aria-hidden className="size-4" strokeWidth={1.75} />
        {[day, range, when].filter(Boolean).join(" · ")}
      </span>
      <span className="home-next__title">{next.title}</span>
      <span className="home-next__kind">
        {translate(`desk.home.agenda.sources.${next.source}`, { _: "" })}
        {next.href ? (
          <ChevronRight aria-hidden className="size-4" strokeWidth={2} />
        ) : null}
      </span>
    </>
  );
  return next.href ? (
    <Link to={next.href} className="home-next">
      {body}
    </Link>
  ) : (
    <div className="home-next">{body}</div>
  );
};

/**
 * Phone home: view A only (no toggle, no greeting: the launch splash says
 * hi). Chips → Siguiente → Necesita atención (overdue included) → Por
 * cobrar.
 */
export const MobileHomeToday = () => {
  const translate = useTranslate();
  const { inbox, counters, collect, deals, isPending, hasInvoices } =
    useHomeToday();
  const t = (key: string) => translate(`desk.home.mobile.${key}`);

  return (
    <div className="home-m">
      <TodayCounters counters={counters} collect={collect} compact />
      <section className="home-m__section" aria-labelledby="home-m-next">
        <SectionHead
          id="home-m-next"
          title={t("next")}
          to={MOBILE_CALENDAR_PATH}
          link={translate("crm.mobile.calendar")}
        />
        <NextCard />
      </section>
      <section className="home-m__section" aria-labelledby="home-m-attention">
        <SectionHead
          id="home-m-attention"
          title={t("attention")}
          to="/tasks"
          link={t("see_all")}
        />
        <div className="home-m__card">
          {isPending ? (
            <AttentionLoading />
          ) : (
            <AttentionGroups
              inbox={inbox}
              counters={counters}
              deals={deals}
              variant="mobile"
              preview={3}
            />
          )}
        </div>
      </section>
      {hasInvoices ? (
        <section className="home-m__section" aria-labelledby="home-m-collect">
          <SectionHead
            id="home-m-collect"
            title={t("collect")}
            to="/invoices"
            link={t("invoices")}
          />
          <CollectCard collect={collect} compact />
        </section>
      ) : null}
    </div>
  );
};
